/**
 * RuleEvaluator — evaluates a transition rule graph to a boolean.
 *
 * Rule graph node types:
 *   varGet    — reads animVariables.variableValues[data.varName]
 *   ruleNot   — boolean NOT of its single input
 *   ruleAnd   — boolean AND of inputs a + b
 *   ruleOr    — boolean OR of inputs a + b
 *   ruleCompare — compares input value against threshold using operator
 *   ruleTimeInState — seconds since the transition's SOURCE state was entered
 *                     (context.stateEnteredAt, a Date.now() stamp). Counts from
 *                     the moment the state was entered, so it includes the
 *                     crossfade into it. No stamp → 0, so a timer rule can
 *                     never fire early off a state it has no clock for.
 *   ruleResult — terminal "Can Enter Transition" node (id always 'ruleResult')
 *
 * Returns true if the ruleResult node's input evaluates to true.
 */

import { variableValues } from './animVariables'
import { debugState } from './debugState'

function evalNode(nodeId, nodes, edges, context) {
  const node = nodes.find(n => n.id === nodeId)
  if (!node) return null

  switch (node.type) {
    case 'varGet': {
      const val = variableValues[node.data?.varName]
      return val ?? null
    }

    // The source state's own player (context.clipTiming), else the clip's
    // per-name timing — a face clip, which has no mixer player.
    case 'ruleAnimTimeRemaining':
      return (context?.clipTiming ?? debugState.clipTimings[context?.currentClipName])?.remaining ?? 999

    case 'ruleAnimTimeRatio':
      return (context?.clipTiming ?? debugState.clipTimings[context?.currentClipName])?.ratio ?? 0

    case 'ruleTimeInState': {
      const at = context?.stateEnteredAt
      return typeof at === 'number' ? (Date.now() - at) / 1000 : 0
    }

    case 'ruleNot': {
      const inEdge = edges.find(e => e.target === nodeId && e.targetHandle === 'in')
      if (!inEdge) return null
      const val = evalNode(inEdge.source, nodes, edges, context)
      return val === null ? null : !val
    }

    case 'ruleAnd': {
      const aEdge = edges.find(e => e.target === nodeId && e.targetHandle === 'a')
      const bEdge = edges.find(e => e.target === nodeId && e.targetHandle === 'b')
      const a = aEdge ? evalNode(aEdge.source, nodes, edges, context) : false
      const b = bEdge ? evalNode(bEdge.source, nodes, edges, context) : false
      return Boolean(a) && Boolean(b)
    }

    case 'ruleOr': {
      const aEdge = edges.find(e => e.target === nodeId && e.targetHandle === 'a')
      const bEdge = edges.find(e => e.target === nodeId && e.targetHandle === 'b')
      const a = aEdge ? evalNode(aEdge.source, nodes, edges, context) : false
      const b = bEdge ? evalNode(bEdge.source, nodes, edges, context) : false
      return Boolean(a) || Boolean(b)
    }

    case 'ruleCompare': {
      const inEdge = edges.find(e => e.target === nodeId && e.targetHandle === 'value')
      if (!inEdge) return false
      const val = evalNode(inEdge.source, nodes, edges, context)
      if (val === null) return false
      const op        = node.data?.operator  ?? '=='
      const threshold = node.data?.threshold ?? 0
      switch (op) {
        case '==': return val == threshold   // eslint-disable-line eqeqeq
        case '!=': return val != threshold   // eslint-disable-line eqeqeq
        case '>':  return val > threshold
        case '<':  return val < threshold
        case '>=': return val >= threshold
        case '<=': return val <= threshold
        default:   return false
      }
    }

    default:
      return null
  }
}

/**
 * Evaluate a rule graph.
 * @param {{ nodes: object[], edges: object[] }} ruleGraph
 * @param {{ currentClipName?: string, clipTiming?: { remaining: number, ratio: number } | null, stateEnteredAt?: number }} [context]
 * @returns {boolean}
 */
export function evaluateRule({ nodes, edges }, context = {}) {
  if (!nodes || nodes.length === 0) return false

  const resultNode = nodes.find(n => n.id === 'ruleResult')
  if (!resultNode) return false

  const inEdge = edges.find(e => e.target === 'ruleResult')
  if (!inEdge) return false

  const val = evalNode(inEdge.source, nodes, edges, context)
  return Boolean(val)
}
